import { IImageGlobalCSProps, IImageProps } from '@/lib/contentstack-sdk/types';
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import { fetchAlgoliaPlans } from '@/lib/data/getHousePlans';
import { IDesignStudio } from '../or-design-studio-block/or-design-studio-block.types';
import {
  IHomePlanCMS,
  IHomePlanData,
  THomePlansResponse,
} from './or-home-plans-carousel.types';

export const getImage = (plan: IHomePlanData) => {
  if (plan?.Image) return plan.Image;
  return {
    src: {
      desktop: '/assets/home_plan.jpeg',
      tablet: '/assets/home_plan.jpeg',
      mobile: '/assets/home_plan.jpeg',
    },
    alt: plan.Name,
  };
};

export const getFeatures = (
  plan: IHomePlanData,
  currentBuildStudio?: IDesignStudio | null,
) => {
  const features = [`${plan.Bedrooms} BED`, `${plan.Bathrooms} BATH`];
  if (plan.Elevation) {
    features.push(`${plan.SquareFeet} SQ.FT`);
  }
  if (plan?.BaseElevationPrice && currentBuildStudio?.Id) {
    features.push(`$${plan.BaseElevationPrice.toLocaleString()} +`);
  }
  return features;
};

/** Normalize helper */
const normalizeToHomePlanData = ({
  Id,
  Name,
  Bedrooms,
  Bathrooms,
  HasGarage,
  IsMultiGen,
  IsBaseElevation,
  Width,
  Depth,
  SquareFeet,
  ExteriorStyle,
  Image,
  imageAlt,
  exteriorCount,
  BaseElevationPrice,
}: any) => ({
  Id,
  Name,
  Bedrooms,
  Bathrooms,
  HasGarage,
  IsMultiGen,
  SquareFeet,
  BaseElevationPrice,
  Elevation: {
    Id,
    Name,
    IsBaseElevation,
    Width,
    Depth,
    ExteriorStyle,
  },
  Stories: [],
  SimilarPlans: [],
  Image: {
    alt: imageAlt,
    src: Image?.src ?? {},
  },
  exteriorCount,
});

/** Matches image data from CMS or defaults to Algolia image */
const getImageDataForPlan = (
  planName: string,
  defaultImage: string,
  matchingCmsPlan?: { image: IImageGlobalCSProps | IImageProps },
) => {
  if (matchingCmsPlan?.image) return parseImageFromCMS(matchingCmsPlan.image);

  return {
    src: {
      mobile: defaultImage,
      tablet: defaultImage,
      desktop: defaultImage,
    },
    alt: planName,
  };
};

/** Parses full plan list from CMS directly */
const parseCMSPlansIntoHomePlans = (
  cmsHomePlans: IHomePlanCMS[],
): THomePlansResponse => ({
  Plans: cmsHomePlans.map((cmsPlan) => {
    const parsedImage = parseImageFromCMS(cmsPlan.plan_image);

    return {
      Plan: normalizeToHomePlanData({
        Id: cmsPlan.home_plan_id,
        Name: cmsPlan.home_plan_name,
        Bedrooms: cmsPlan.bedrooms,
        Bathrooms: cmsPlan.bathrooms,
        HasGarage: cmsPlan.has_garage,
        IsMultiGen: cmsPlan.is_multi_gen,
        IsBaseElevation: cmsPlan.is_base_elevation,
        Width: cmsPlan.width,
        Depth: cmsPlan.depth,
        SquareFeet: cmsPlan.width * cmsPlan.depth,
        BaseElevationPrice: null,
        ExteriorStyle: {
          Id: 0,
          Name: cmsPlan.exterior_style.exterior_name,
        },
        Image: parsedImage,
        imageAlt: cmsPlan.home_plan_name,
        exteriorCount: cmsPlan.number_of_exteriors || 1,
      }),
    };
  }),
});

/** Fetches Algolia plans and maps to response shape */
export const convertCmsPlansIntoAlgoliaPlans = async (
  cmsPlans: {
    hpId: string;
    image: IImageGlobalCSProps | IImageProps;
    exteriorStyleId?: string;
  }[],
  studioId?: number,
): Promise<THomePlansResponse> => {
  const ids = cmsPlans.map((p) => p.hpId);

  const algoliaPlans = await fetchAlgoliaPlans(ids, studioId);

  const plansById = algoliaPlans.reduce(
    (acc, plan) => {
      const planId = plan.Plan.Id;
      acc[planId] = acc[planId] || [];
      acc[planId].push(plan);
      return acc;
    },
    {} as Record<number, typeof algoliaPlans>,
  );

  const baseElevationPrices = Object.entries(plansById).reduce(
    (acc, [planId, elevations]) => {
      const base = elevations.find((e) => e.IsBaseElevation);
      acc[Number(planId)] = base?.FloorPlans?.[0]?.BasePrice || null;
      return acc;
    },
    {} as Record<number, number | null>,
  );

  const filteredPlans = algoliaPlans
    .filter((plan) =>
      cmsPlans.some(
        (cms) =>
          Number(cms.hpId) === plan.Plan.Id &&
          Number(cms.exteriorStyleId) === plan.ExteriorStyle?.Id,
      ),
    )
    .map((plan) => {
      const matchCms = cmsPlans.find(
        (cms) =>
          Number(cms.hpId) === plan.Plan.Id &&
          Number(cms.exteriorStyleId) === plan.ExteriorStyle?.Id,
      );

      const img = getImageDataForPlan(plan.Plan.Name, plan.Image, matchCms);
      return {
        Plan: normalizeToHomePlanData({
          Id: plan.Plan.Id,
          Name: plan.Plan.Name,
          Bedrooms: plan.Plan.Bedrooms,
          Bathrooms: plan.Plan.Bathrooms,
          HasGarage: plan.Plan.HasGarage,
          IsMultiGen: plan.Plan.IsMultiGen,
          IsBaseElevation: plan.IsBaseElevation,
          Width: plan.Width,
          Depth: plan.Depth,
          SquareFeet: plan.SquareFeet,
          ExteriorStyle: plan.ExteriorStyle,
          Image: img,
          imageAlt: plan.Plan.Name,
          exteriorCount: plansById[plan.Plan.Id]?.length || 1,
          BaseElevationPrice: baseElevationPrices[plan.Plan.Id],
        }),
      };
    });

  const cmsOrder = cmsPlans.map((cms) => Number(cms.hpId));

  filteredPlans.sort((a, b) => {
    const idA = a.Plan.Id;
    const idB = b.Plan.Id;
    return cmsOrder.indexOf(idA) - cmsOrder.indexOf(idB);
  });
  return { Plans: filteredPlans };
};
